import { rfidTagApi } from "#/api/rfidTagApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import SelectableTable from "#/components/tables/SelectableTable";
import type { TableSortProps } from "#/components/tables/BaseTable";
import type { RfidTag } from "#/types/rfidTag";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import { exportToCSV } from "#/utils/CsvUtils";
import {
  Badge,
  Button,
  SegmentedControl,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBox,
  IconCar,
  IconDeviceMobile,
  IconDownload,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import BulkCreateItemsModal from "./components/BulkCreateItemsModal";
import BulkDeleteModal from "./components/BulkDeleteModal";
import DeleteRfidTagModal from "./components/DeleteRfidTagModal";
import EditRfidTagModal from "./components/EditRfidTagModal";
import RfidLinkCard from "./components/RfidLinkCard";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const RfidTagsPage = () => {
  const { t } = useTranslation();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [activeTab, setActiveTab] = useState<"linked" | "not_linked">("linked");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [search, setSearch] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [selectedRfidIds, setSelectedRfidIds] = useState<number[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    bulkDeleteModalOpened,
    { open: openBulkDeleteModal, close: closeBulkDeleteModal },
  ] = useDisclosure(false);
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] =
    useDisclosure(false);
  const [
    bulkCreateModalOpened,
    { open: openBulkCreateModal, close: closeBulkCreateModal },
  ] = useDisclosure(false);
  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);
  const [rfidTagToDelete, setRfidTagToDelete] = useState<RfidTag>();
  const [rfidTagToLink, setRfidTagToLink] = useState<RfidTag>();

  // Headers for Linked tab
  const linkedHeaders = [
    { key: "rfid", value: t("rfidTags.columns.rfid") },
    { key: "type", value: t("rfidTags.columns.type") },
    { key: "linked_to", value: t("rfidTags.columns.linkedTo") },
    { key: "last_scanned_by", value: t("rfidTags.columns.scanner") },
    { key: "createdAt", value: t("rfidTags.columns.createdAt") },
    { key: "updatedAt", value: t("rfidTags.columns.lastUpdated") },
    { key: "actions", value: t("common.actions") },
  ];

  // Headers for Not Linked tab
  const notLinkedHeaders = [
    { key: "rfid", value: t("rfidTags.columns.rfid") },
    { key: "type", value: t("rfidTags.columns.type") },
    { key: "last_scanned_by", value: t("rfidTags.columns.scanner") },
    { key: "createdAt", value: t("rfidTags.columns.createdAt") },
    { key: "updatedAt", value: t("rfidTags.columns.lastUpdated") },
    { key: "actions", value: t("common.actions") },
  ];

  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getRfidTags,
  } = useQuery({
    queryKey: ["rfidTags", sortBy, page, sortDirection, search, activeTab],
    queryFn: async () => {
      try {
        const response = await rfidTagApi.getAll({
          limit: limit,
          page: page,
          ...(search ? { search: search } : {}),
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
          status: activeTab,
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No RFID tags returned");
      }
    },
    staleTime: 1000 * 60,
    retry: false,
  });


  const getTypeColor = (type: string) => {
    switch (type) {
      case "hard":
        return "blue";
      case "label":
        return "orange";
      default:
        return "gray";
    }
  };

  const getDeviceTypeShort = (hardwareType?: string | null) => {
    if (!hardwareType) return "";
    // chainway_c72 -> C72, sgs_rpi -> RPI
    const parts = hardwareType.split("_");
    return parts[parts.length - 1].toUpperCase();
  };

  const renderLinkedTo = (rfidTag: RfidTag) => {
    if (rfidTag.item) {
      return (
        <RfidLinkCard
          variant="item"
          label={t("tickets.form.item")}
          path={`/inventory/items/${rfidTag.item.id}`}
          icon={<IconBox size={14} />}
        />
      );
    }
    if (rfidTag.vehicle) {
      return (
        <RfidLinkCard
          variant="vehicle"
          label={t("vehicles.title")}
          path={`/fleet/vehicles/${rfidTag.vehicle.id}`}
          icon={<IconCar size={14} />}
        />
      );
    }
    return "--";
  };

  const renderScanner = (rfidTag: RfidTag) => {
    if (!rfidTag.lastReadByDevice) {
      return "--";
    }

    return (
      <RfidLinkCard
        variant="scanner"
        label={rfidTag.lastReadByDevice.name}
        path={`/devices/${rfidTag.lastReadByDevice.id}`}
        icon={<IconDeviceMobile size={14} />}
        deviceType={getDeviceTypeShort(rfidTag.lastReadByDevice.hardware_type)}
      />
    );
  };

  const prepareExportData = (itemsToExport: typeof items) => {
    return itemsToExport.map((item) => ({
      rfid: item.rfid || "",
      type: item.type || "",
      status: item.status || "",
      linked_to: item.item ? "Item" : item.vehicle ? "Vehicle" : "Not Linked",
      linked_item_id: item.item?.id || "",
      linked_vehicle_id: item.vehicle?.id || "",
      createdAt: formatDateTimeForExport(item.createdAt),
      updatedAt: formatDateTimeForExport(item.updatedAt),
    }));
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const headers = [
      "rfid",
      "type",
      "status",
      "linked_to",
      "linked_item_id",
      "linked_vehicle_id",
      "createdAt",
      "updatedAt",
    ];

    const headerLabels = {
      rfid: "RFID",
      type: "Type",
      status: "Status",
      linked_to: "Linked To",
      linked_item_id: "Linked Item ID",
      linked_vehicle_id: "Linked Vehicle ID",
      createdAt: "Created At",
      updatedAt: "Last Updated",
    };

    const note = "NOTE: All dates and times in this export are in UTC (Coordinated Universal Time) format.\nFormat: DD/MM/YYYY HH:mm (Day/Month/Year Hour:Minute in 24-hour format)";
    const filename = `rfid-tags-${activeTab}-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, headers, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      const response = await rfidTagApi.getAll({
        limit: total,
        page: 1,
        ...(search ? { search: search } : {}),
        ...(sortBy ? { sortBy: sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
        status: activeTab,
      });

      const allItems = response.data.data;
      const exportData = prepareExportData(allItems);

      const headers = [
        "rfid",
        "type",
        "status",
        "linked_to",
        "linked_item_id",
        "linked_vehicle_id",
        "createdAt",
        "updatedAt",
      ];

      const headerLabels = {
        rfid: "RFID",
        type: "Type",
        status: "Status",
        linked_to: "Linked To",
        linked_item_id: "Linked Item ID",
        linked_vehicle_id: "Linked Vehicle ID",
        createdAt: "Created At",
        updatedAt: "Last Updated",
      };

      const note = "NOTE: All dates and times in this export are in UTC (Coordinated Universal Time) format.\nFormat: DD/MM/YYYY HH:mm (Day/Month/Year Hour:Minute in 24-hour format)";
      const filename = `rfid-tags-${activeTab}-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, headers, filename, headerLabels, note);
    } catch (error) {
      console.error("Failed to export all RFID tags:", error);
    }
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4 relative">
      <div className="flex  flex-col gap-4 bg-white z-10">
        <div className="flex items-center gap-2">
          <Title order={2}>{t("rfidTags.title")}</Title>
          <SegmentedControl
            data={[
              { label: t("rfidTags.linked"), value: "linked" },
              { label: t("rfidTags.notLinked"), value: "not_linked" },
            ]}
            value={activeTab}
            onChange={(value) => {
              setActiveTab(value as "linked" | "not_linked");
              setPage(1);
              setSelectedRfidIds([]);
              setSelectedIndices([]);
            }}
            size="xs"
          />
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            className="ml-auto"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
          >
            {t("common.exportCsv")}
          </Button>
        </div>
        <div className="w-full flex items-center justify-between gap-4">
          <TextInput
            className="flex-1 max-w-96"
            placeholder={t("rfidTags.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
          />
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === "linked" && (
          <div className="flex-1 overflow-auto">
            <SelectableTable
              headers={linkedHeaders.map((item) => item.value)}
              loading={isPending}
              selectedIndices={selectedIndices}
              onSelectionChange={(newIndices) => {
                const selectedTags = newIndices.map((index) => items[index]);
                const ids = selectedTags.map((tag) => tag.id);
                setSelectedRfidIds(ids);
                setSelectedIndices(newIndices);
              }}
              allowSorting={true}
              sortableColumns={[1, 2, 4, 5, 6]}
              onSortChange={(e: TableSortProps) => {
                setPage(1);
                setSortDirection(e.direction);
                setSortBy(linkedHeaders[e.index - 1].key);
              }}
              defaultSort={{
                direction: "DESC",
                index: 6,
              }}
              rows={items.map((item) => {
                return (
                  <Table.Tr key={item.id}>
                    <Table.Td className="font-mono">
                      {item.rfid ?? "--"}
                    </Table.Td>
                    <Table.Td className="w-[125px]">
                      <Badge
                        color={getTypeColor(item.type)}
                        variant="light"
                        className="capitalize"
                      >
                        {item.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td className="w-[200px] min-h-[60px]">{renderLinkedTo(item)}</Table.Td>
                    <Table.Td className="w-[300px] min-h-[60px]">{renderScanner(item)}</Table.Td>
                    <Table.Td className="w-[150px]">
                      {item.createdAt ? (
                        <span>
                          {formatDate(item.createdAt, client)}
                          <br />
                          {formatTime(item.createdAt, client)}
                        </span>
                      ) : (
                        "--"
                      )}
                    </Table.Td>
                    <Table.Td className="w-[150px]">
                      {item.updatedAt ? (
                        <span>
                          {formatDate(item.updatedAt, client)}
                          <br />
                          {formatTime(item.updatedAt, client)}
                        </span>
                      ) : (
                        "--"
                      )}
                    </Table.Td>
                    <Table.Td className="min-w-[100px] w-[100px]">
                      <div className="w-full flex items-center justify-center gap-2">
                        <PermissionTooltip disabled={!canWrite()}>
                          <ActionButton
                            icon={IconPencil}
                            variant="outline"
                            onClick={() => {
                              setRfidTagToLink(item);
                              openLinkModal();
                            }}
                            disabled={!canWrite()}
                          />
                        </PermissionTooltip>
                        <PermissionTooltip disabled={!canDelete()}>
                          <ActionButton
                            icon={IconTrash}
                            variant="outline"
                            color="red"
                            onClick={() => {
                              setRfidTagToDelete(item);
                              openDeleteModal();
                            }}
                            disabled={!canDelete()}
                          />
                        </PermissionTooltip>
                      </div>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            />
          </div>
        )}

        {activeTab === "not_linked" && (
          <div className="flex-1 overflow-auto">
            <SelectableTable
              headers={notLinkedHeaders.map((item) => item.value)}
              loading={isPending}
              selectedIndices={selectedIndices}
              onSelectionChange={(newIndices) => {
                const selectedTags = newIndices.map((index) => items[index]);
                const ids = selectedTags.map((tag) => tag.id);
                setSelectedRfidIds(ids);
                setSelectedIndices(newIndices);
              }}
              allowSorting={true}
              sortableColumns={[1, 2, 3, 4, 5]}
              onSortChange={(e: TableSortProps) => {
                setPage(1);
                setSortDirection(e.direction);
                setSortBy(notLinkedHeaders[e.index - 1].key);
              }}
              defaultSort={{
                direction: "DESC",
                index: 5,
              }}
              rows={items.map((item) => {
                return (
                  <Table.Tr key={item.id}>
                    <Table.Td className="font-mono">
                      {item.rfid ?? "--"}
                    </Table.Td>
                    <Table.Td className="w-[125px]">
                      <Badge
                        color={getTypeColor(item.type)}
                        variant="light"
                        className="capitalize"
                      >
                        {item.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td className="w-[300px] min-h-[60px]">{renderScanner(item)}</Table.Td>
                    <Table.Td className="w-[150px]">
                      {item.createdAt ? (
                        <span>
                          {formatDate(item.createdAt, client)}
                          <br />
                          {formatTime(item.createdAt, client)}
                        </span>
                      ) : (
                        "--"
                      )}
                    </Table.Td>
                    <Table.Td className="w-[150px]">
                      {item.updatedAt ? (
                        <span>
                          {formatDate(item.updatedAt, client)}
                          <br />
                          {formatTime(item.updatedAt, client)}
                        </span>
                      ) : (
                        "--"
                      )}
                    </Table.Td>
                    <Table.Td className="min-w-[100px] w-[100px]">
                      <div className="w-full flex items-center justify-center gap-2">
                        <PermissionTooltip disabled={!canWrite()}>
                          <ActionButton
                            icon={IconPencil}
                            variant="outline"
                            onClick={() => {
                              setRfidTagToLink(item);
                              openLinkModal();
                            }}
                            disabled={!canWrite()}
                          />
                        </PermissionTooltip>
                        <PermissionTooltip disabled={!canDelete()}>
                          <ActionButton
                            icon={IconTrash}
                            variant="outline"
                            color="red"
                            onClick={() => {
                              setRfidTagToDelete(item);
                              openDeleteModal();
                            }}
                            disabled={!canDelete()}
                          />
                        </PermissionTooltip>
                      </div>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            />
          </div>
        )}
      </div>

      <DeleteRfidTagModal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        rfidTag={rfidTagToDelete}
        onSuccess={() => {
          setRfidTagToDelete(undefined);
          void getRfidTags();
        }}
      />

      <BulkDeleteModal
        opened={bulkDeleteModalOpened}
        onClose={closeBulkDeleteModal}
        selectedTags={items.filter((item) => selectedRfidIds.includes(item.id))}
        onSuccess={() => {
          setSelectedRfidIds([]);
          setSelectedIndices([]);
          void getRfidTags();
        }}
      />

      <EditRfidTagModal
        opened={linkModalOpened}
        onClose={closeLinkModal}
        rfidTag={rfidTagToLink}
        onSuccess={() => {
          setRfidTagToLink(undefined);
          void getRfidTags();
        }}
      />

      <BulkCreateItemsModal
        opened={bulkCreateModalOpened}
        onClose={closeBulkCreateModal}
        selectedTags={items.filter((item) => selectedRfidIds.includes(item.id))}
        onSuccess={() => {
          setSelectedRfidIds([]);
          setSelectedIndices([]);
          void getRfidTags();
        }}
      />

      {selectedRfidIds.length > 0 && (
        <div className=" absolute bottom-0 left-0 inset-x-0 z-50 flex items-center justify-between w-full bg-black/10 border-t border-black/50 backdrop-blur-sm p-4 ">
          <div className="flex items-center gap-2">
            <Text size="md" fw={600} c="dark">
              {selectedRfidIds.length} {t("common.selected")}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="filled"
                size="md"
                onClick={() => {
                  openBulkDeleteModal();
                }}
                disabled={!canDelete()}
              >
                {t("rfidTags.deleteSelected")}
              </Button>
            </PermissionTooltip>
            {activeTab === "not_linked" && (
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  leftSection={<IconPlus size={16} />}
                  color="black"
                  variant="filled"
                  size="md"
                  onClick={() => {
                    openBulkCreateModal();
                  }}
                  disabled={!canWrite()}
                >
                  {t("rfidTags.createItems")}
                </Button>
              </PermissionTooltip>
            )}
          </div>
        </div>
      )}

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default RfidTagsPage;
